import { message } from 'antd';
import { getGetAzioniDocumentoQueryKey } from 'client/api/backend/attivita/attivita';
import {
  BEResponseListFirmaResponseDTO,
  Certificato,
  FirmaRequest
} from 'client/api/backend/schemas';
import { queryClient } from 'client/core/network/queryClient';
import { groupBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import {
  ProviderParameters,
  RemoteSignType
} from '../../provider-parameters/ProviderParameters';
import { IRemoteSignErrorBag } from '../../reducer/RemoteSignActions';
import { RemoteSignStep } from '../../reducer/RemoteSignReducer';
import { useRemoteSignApi } from './useRemoteSignApi';

interface IParams {
  provider: ProviderParameters | null | undefined;
  signType: RemoteSignType | null | undefined;
  certificate: Certificato | null | undefined;
  documentId: string;
  taskId: string;
  setCurrentStep: (step: RemoteSignStep) => void;
  setErrors: (errorBag: IRemoteSignErrorBag) => void;
  flIntermedio?: boolean;
  flCompletare?: boolean;
}

/**
 * Hook per la gestione della firma remota di un singolo documento.
 */
export function useSingleRemoteSign(params: IParams) {
  const {
    documentId,
    taskId,
    provider,
    signType,
    certificate,
    flCompletare,
    flIntermedio,
    setCurrentStep,
    setErrors
  } = params;

  const requestData = useMemo<FirmaRequest>(() => {
    if (!signType) return { firmeRequest: [] };

    return {
      firmeRequest: [
        {
          idDocumento: documentId,
          idTaskInstance: taskId,
          flIntermedio: Boolean(flIntermedio),
          flCompletare: Boolean(flCompletare),
          tipo: signType,
          idCertificato: certificate?.id
        }
      ]
    };
  }, [documentId, taskId, signType, certificate, flCompletare, flIntermedio]);

  const handleRemoteSign = useRemoteSignApi<
    BEResponseListFirmaResponseDTO,
    FirmaRequest
  >({
    method: 'POST',
    fields: provider?.remoteSignParameters ?? [],
    apiUrl: `/firma-remota/firma/${provider?.webserviceId}`,
    skip: !provider
  });

  const handleSubmit = useCallback(
    async (values: any) => {
      try {
        const result = await handleRemoteSign(values, requestData);

        // Se ritorna 1 solo elemento, non proseguo ma mostro direttamente l'errore.
        if (result?.data?.length === 1) {
          result?.data?.forEach(response => {
            if (response.error) {
              throw new Error(response.errorMessage);
            }
          });
        } else {
          // Se ci sono errori, li raccolgo in un array così da mostrarli
          const resultWithError = result?.data?.filter(
            response => response.error
          );

          const grouped = groupBy(resultWithError, 'idTaskInstance');

          const errorBag = Object.keys(grouped).map(taskId => ({
            taskId,
            messages: grouped[taskId].map(response => response.errorMessage!)
          }));

          setErrors(errorBag);
        }

        queryClient.invalidateQueries(getGetAzioniDocumentoQueryKey(taskId));

        setCurrentStep(RemoteSignStep.Signing);
      } catch (e: any) {
        console.error(`[RemoteSignCertificateStep] Si è verificato un errore durante il processo di firma`, {error: e}); // prettier-ignore
        const errorMessage = e?.response?.data?.esito?.errore.messaggioDiErrore ?? e.message; // prettier-ignore
        message.error(`Si è verificato un errore durante il processo di firma: ${errorMessage}`); // prettier-ignore
        return;
      }
    },
    [
      handleRemoteSign,
      setCurrentStep,
      setErrors,
      requestData,
      taskId,
      queryClient
    ]
  );

  return handleSubmit;
}
